import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import PageHero from '../components/PageHero';

const About: React.FC = () => {
  const { t } = useTranslation('about');
  const defaultStory = `Jmenuju se AlphaLuk. Možná mě znáš z internetu, z projektů, z videí, nebo z mé tvorby. Ale málokdo ví, jaký příběh se skrývá za tím jménem. Můj život nebyl jednoduchý. A i když se snažím usmívat, tvořit, být silný, ve skutečnosti za tím vším stojí roky bolesti, nepochopení a boje se sebou samým.

**Moje problémy začaly, když mi bylo osm let.** Bylo to období, kdy děti běhají po hřišti, smějí se a nemají žádné starosti. Jenže u mě to bylo jiné. Začaly se objevovat tiky, závratě a podivné stavy, které nikdo pořádně nechápal. Lidi kolem mě si mysleli, že to jen přeháním, že si vymýšlím. Ale já jsem věděl, že to není jen v hlavě.

**Nejhorší na tom bylo, že ti, kdo by měli dítě chránit – učitelé – se ke mně otočili zády.** Dokonce i ti, kterým jsem věřil. Pamatuju si, jak se mi jednou při tělocviku strašně zatočila hlava. Zastavil jsem se, začal jsem brečet, protože jsem nevěděl, co se se mnou děje. A místo pomoci jsem slyšel jen: „Přestaň si vymýšlet, pokračuj!" Ten moment se mi vypálil do paměti. Cítil jsem se tehdy bezmocně. Malý kluk, který nechápal, proč mu nikdo nevěří.

**Když se to všechno zhoršovalo, přidaly se tiky a další příznaky.** Bylo mi řečeno, že mám podezření na epilepsii. Později i potvrzenou lehčí formu. A tak se můj život změnil. Najednou jsem musel přemýšlet o věcech, které jiné děti vůbec neřeší – jak moc se můžu namáhat, jestli se mi nezatočí hlava, jestli někde neomdlím. Nakonec jsem přestoupil na jinou školu, protože ta stará se pro mě stala peklem. Ale i tam jsem musel znovu dokazovat, že si nic nevymýšlím. Naštěstí se tam objevily dvě učitelky, které mi skutečně věřily. Nechtěl bych je jmenovat, ale právě ony mi dodaly sílu jít dál.

**Teď je mi devatenáct. Žiju s diagnózou, která ovlivňuje můj každodenní život.** Někdy se mi motá hlava, někdy mám dny, kdy prostě nemůžu. Ven moc nechodím sám, protože nikdy nevím, co se může stát. Ale i přesto se snažím dělat to, co miluju – tvořit. Tvorba je pro mě víc než jen koníček. Je to způsob, jak se vyrovnávám se světem. Když stříhám videa, když pracuju na projektech, cítím se svobodný. Jsem součástí EchoDabing, kde pomáhám s videi a propagačními materiály. Mám svůj Minecraft survival server, který ještě vyvíjím, a taky dělám na React projektech, které mě fakt baví. Baví mě posouvat věci dál, zkoušet nové nápady, tvořit vlastní cesty.

**Nejsem obyčejný člověk, který se jen veze životem.** Musel jsem si vybudovat sílu sám. Moji rodiče jsou pro mě obrovskou oporou – stojí za mnou i ve chvílích, kdy už sám nevím, jestli mám sílu pokračovat. Mám taky jednoho skvělého kamaráda, který mě nikdy neopustil, a svou psycholožku, která mi pomáhá chápat věci, které mě dlouho ničily zevnitř. Ale i tak je ta podpora někdy slabá, protože většinu mých bojů musím vybojovat já sám.

**Nikdy nezapomenu na to, jak se lidi dokážou posmívat něčemu, čemu nerozumí.** Když máš problém, který není vidět, lidé tě často odsoudí. A právě to mě naučilo být silnější. Naučilo mě to chápat ostatní. Vím, jaké to je být sám. Vím, jaké to je, když tě nikdo nechápe. A proto chci, aby můj příběh pomohl ostatním. Chci, aby lidi věděli, že i když svět není fér, nikdy nesmíš přestat věřit. Někdy to bolí, někdy to trvá roky, ale cesta existuje. A i když máš diagnózu, tiky, závratě, nebo jiné problémy – nejsi slabý. Naopak. Jsi silný, protože vstáváš každé ráno a bojuješ.

**Můj cíl je být inspirací.** Ne proto, že chci slávu. Ale protože chci ukázat, že i člověk, kterého život shodil na dno, může zvednout hlavu a jít dál. Chci, aby moje videa a projekty měly smysl, aby v nich lidi našli kousek naděje, odvahy nebo aspoň úsměv. Dnes už vím, že minulost nezměním. Ale můžu z ní čerpat sílu. Všechno, co jsem prožil – bolesti, nepochopení, pády – mě posílilo. A i když mám pořád dny, kdy je to těžké, už se nebojím. Protože vím, kdo jsem. Jsem AlphaLuk. Jsem člověk, který se nevzdal. A nikdy se nevzdám.

**A pokud si tohle čte někdo, kdo prožívá něco podobného – pamatuj si tohle:** nejsi sám. Možná tě lidi nechápou, možná tě soudí, ale to neznamená, že tvůj boj není skutečný. Nevzdávej to. Protože i když máš pocit, že jsi sám ve tmě, můžeš být světlem pro ostatní.`;

  const story = t('storyContent', { defaultValue: defaultStory });


  return (
      <div className="min-h-screen pt-24">
        <section className="page-section relative overflow-hidden">
          <div className="container mx-auto px-6">
            <PageHero title={t('heroTitle')} subtitle={t('heroSubtitle')} />
          </div>
        </section>

        {/* Story Content */}
        <section className="page-section">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="page-max rounded-2xl border border-dark-600/50 bg-dark-800/25 p-6 shadow-xl backdrop-blur-sm md:p-10"
            >
              <div className="relative">
                <div className="absolute -left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full opacity-50" />
                <div className="text-base text-dark-300 leading-loose pl-6 border-l-2 border-dark-600 prose prose-invert max-w-none">
                  <ReactMarkdown
                    components={{
                      strong: ({ node, ...props }) => <strong className="text-purple-200" {...props} />,
                      p: ({ node, ...props }) => <p className="mb-6" {...props} />
                    }}
                  >
                    {story}
                  </ReactMarkdown>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="flex space-x-2 mt-8">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.8 + i * 0.1 }}
                    viewport={{ once: true }}
                    className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-purple-600"
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Tourette Syndrome Book Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <div className="text-center mb-12">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 rounded-2xl mb-6 shadow-lg"
                >
                  <span className="text-3xl">📚</span>
                </motion.div>
                <h2 className="text-4xl md:text-6xl font-display font-bold gradient-text mb-4">
                  {t('bookSectionTitle')}
                </h2>
                <p className="text-xl text-dark-300 max-w-3xl mx-auto leading-relaxed">
                  {t('bookSectionSubtitle')}
                </p>
                <div className="w-20 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto mt-4" />
              </div>

              <div className="relative group">
                <div className="relative bg-dark-800/30 backdrop-blur-sm rounded-2xl p-8 border border-primary-500/20 shadow-lg">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      viewport={{ once: true }}
                      className="space-y-6"
                    >
                      <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500/20 to-purple-600/20 rounded-full border border-purple-500/30">
                        <span className="text-purple-400 font-semibold text-sm">{t('bookBadge')}</span>
                      </div>
                      
                      <h3 className="text-3xl md:text-4xl font-display font-bold gradient-text leading-tight">
                        {t('bookTitle')}
                      </h3>
                      
                      <p className="text-lg text-dark-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('bookLead') }} />
                      
                      <p className="text-dark-300 leading-relaxed">
                        {t('bookDescription')}
                      </p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                      viewport={{ once: true }}
                      className="flex flex-col items-center space-y-6"
                    >
                      <div className="relative">
                        <div className="w-48 h-64 bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-2xl shadow-2xl flex items-center justify-center">
                          <div className="text-center space-y-3">
                            <div className="text-6xl">📖</div>
                            <div className="text-purple-200 font-bold text-lg">{t('cardTitle')}</div>
                            <div className="text-purple-200 text-sm">{t('cardSubtitle')}</div>
                          </div>
                        </div>
                        <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-purple-200 font-bold shadow-lg">
                          ⭐
                        </div>
                      </div>
                      
                      <a 
                        href="https://www.knihydobrovsky.cz/kniha/zijeme-s-touretteovym-syndromem-702036391"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-purple-200 font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:scale-105"
                      >
                        <span className="mr-3">{t('buttonText')}</span>
                        <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Personal Note */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="relative p-10 rounded-2xl bg-dark-800/30 backdrop-blur-sm border border-primary-500/20">
                <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-purple-200 text-xl font-bold shadow-lg">
                  💭
                </div>
                
                <h3 className="text-3xl font-display font-bold gradient-text mb-6 mt-4">
                  {t('personalNoteTitle')}
                </h3>
                
                <p className="text-lg text-dark-200 leading-relaxed mb-6">
                  {t('personalNoteP1')}
                </p>
                <p className="text-lg text-dark-200 leading-relaxed mb-6">
                  {t('personalNoteP2')}
                </p>
                <p className="text-lg text-dark-200 leading-relaxed">
                  {t('personalNoteP3')}
                </p>

                <div className="mt-8 flex justify-center">
                  <div className="w-16 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
};

export default About;

